import hotkey from '@atlas/tool/box/event/hotkey';
import useControlledState from '@atlas/tool/box/hooks/useControlledState';
import useMemoState from '@atlas/tool/box/hooks/useMemoState';
import Menu, { type TMenuItem, type TMenuProps, type TMenuRef } from '@atlas/uicorn/lib/Control/Menu';
import type { TOptionsListChooseHandler } from '@atlas/uicorn/lib/Control/OptionsList';
import HotkeyLabel from '@atlas/uicorn/lib/Display/HotkeyLabel';
import Popover, { type TPopoverProps, type TPopoverRef } from '@atlas/uicorn/lib/Display/Popover';
import type { TExample } from '@atlas/uicorn/preview/modules';
import { useImperativeHandle, useRef, useState } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import useRefCallback from 'use-ref-callback';

const ContextMenu = React.memo(
    React.forwardRef(function ContextMenu<TOption extends TContextMenuItem>(
        props: TContextMenuProps<TOption>,
        ref: React.ForwardedRef<TContextMenuRef>,
    ): React.ReactElement {
        const {
            className,
            children,
            items,
            search,
            debounceSearch,
            disabled,
            tooltip,
            placement,
            strategy,
            onChoose,
            onSelect,
            onSelectCompleted,
            onCreate,
            open: propsOpen,
            onOpen,
            onClose,
            onOpenChange,
            defaultOpen,
            defaultSelected,
            selected: propsSelected,
            trigger,
            popoverMaxHeight,
            menuMaxHeight,
            ...optionsMenuProps
        } = props;

        const [menuRef, setMenuRef] = useState<TMenuRef | null>(null);
        const popoverRef = useRef<TPopoverRef>(null);

        useImperativeHandle(
            ref,
            () => ({
                ...menuRef,
                isOpen: () => popoverRef.current?.isOpen() ?? false,
                open: () => popoverRef.current?.open(),
                close: () => popoverRef.current?.close(),
                toggle: () => popoverRef.current?.toggle(),
                getDialog: () => popoverRef.current?.getDialog() || null,
            }),
            [menuRef, popoverRef],
        );

        const hasSelection = !!(onSelect || onSelectCompleted);

        const [open, setOpen] = useControlledState(propsOpen, onOpenChange, defaultOpen);

        const [intermediatelySelected, setIntermediatelySelected] = useMemoState<TOption[] | undefined>(
            (previous) => {
                if (open) return onSelectCompleted ? previous : undefined;
                if (previous) onSelectCompleted?.(previous);
            },
            [open, onSelectCompleted],
        );

        // This is required here to persist state of the Menu that will be destroyed when Popover is closed
        const [selected, setSelected] = useControlledState(
            intermediatelySelected || propsSelected,
            undefined,
            defaultSelected,
        );

        const handleSelect = useRefCallback(
            (selectedItems: TOption[], itemInAction: TOption, addedToSelection: boolean) => {
                onSelect?.(selectedItems, itemInAction, addedToSelection);

                setSelected(selectedItems);
                if (onSelectCompleted) setIntermediatelySelected(selectedItems);
            },
        );

        const handleChoose = useRefCallback((item: TOption, selection: boolean) => {
            if (!selection) popoverRef.current?.close();
            onChoose?.(item, selection);
        });

        const handleCreate = useRefCallback((query?: string) => {
            popoverRef.current?.close();
            onCreate?.(query);
        });

        const handleMenuRender = useRefCallback((menu: null | TMenuRef) => {
            setMenuRef(menu);
            menu?.setCursor(selected?.[0]?.key ?? undefined);
        });

        return (
            <SContextMenuPopover
                className={className}
                content={
                    open && !disabled ? (
                        <SContextMenu<TOption>
                            {...optionsMenuProps}
                            {...{ $menuMaxHeight: menuMaxHeight }}
                            items={items}
                            search={search}
                            debounceSearch={debounceSearch}
                            autoFocus
                            keepCursor
                            defaultSelected={defaultSelected}
                            selected={selected}
                            onChoose={handleChoose}
                            onSelect={(hasSelection && handleSelect) || undefined}
                            onCreate={onCreate && handleCreate}
                            ref={handleMenuRender}
                        />
                    ) : null
                }
                tooltip={tooltip}
                placement={placement}
                strategy={strategy}
                open={open}
                defaultOpen={defaultOpen}
                onOpen={onOpen}
                onClose={onClose}
                onOpenChange={setOpen}
                children={children}
                trigger={trigger}
                ref={popoverRef}
                $popoverMaxHeight={popoverMaxHeight}
            />
        );
    }),
);

export default ContextMenu;

// #region Styles

const SContextMenu = styled(Menu)<{ $menuMaxHeight?: string }>`
    max-width: 100%;
    min-width: 10rem;
    width: 100%;
    max-height: ${({ $menuMaxHeight }) => $menuMaxHeight || '25.5rem'};
` as typeof Menu;

const SContextMenuPopover = styled(Popover)<{ $popoverMaxHeight?: string }>`
    max-width: 30rem;
    max-height: ${({ $popoverMaxHeight }) => $popoverMaxHeight || 'min(90%, 50rem)'};
    display: flex;
    z-index: 1060; // +10 to Ant popovers
`;

// #endregion

// #region Types

export type TContextMenuRef = TPopoverRef | (TPopoverRef & TMenuRef);

export type TContextMenuItem = TMenuItem;

export type TContextMenuSearch<TOption extends TContextMenuItem = TContextMenuItem> = TMenuProps<TOption>['search'];

export type TContextMenuProps<TOption extends TContextMenuItem = TContextMenuItem> = Omit<
    React.HTMLAttributes<HTMLDivElement>,
    'children' | 'onSelect' | 'title'
> &
    Omit<TMenuProps<TOption>, 'search' | 'onChoose'> &
    Pick<
        TPopoverProps,
        | 'className'
        | 'children'
        | 'tooltip'
        | 'open'
        | 'onOpen'
        | 'onClose'
        | 'onOpenChange'
        | 'placement'
        | 'trigger'
        | 'strategy'
    > & {
        search?: null | boolean | string | TContextMenuSearch<TOption>;
        debounceSearch?: boolean | number;
        defaultOpen?: boolean;
        onChoose?: TOptionsListChooseHandler<TOption>;
        onSelectCompleted?: (selectedOptions: TOption[]) => void;
        popoverMaxHeight?: string;
        menuMaxHeight?: string;
    };

// #endregion

// #region Examples

export const Example: TExample = {
    render(props) {
        return (
            <ContextMenu
                items={[
                    { key: 'a', label: 'Option A' },
                    { key: 'b', label: 'Option B' },
                    { key: 'c', label: 'Link C', href: 'https://atlas.so' },
                ]}
                onChoose={props.onChoose}
                onOpen={props.onOpen}
                onClose={props.onClose}
            >
                <button children="click me" />
            </ContextMenu>
        );
    },
    handlers: {
        onChoose: 'console',
        onOpen: 'console',
        onClose: 'console',
    },
    figma: 'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=0-1&mode=design&t=Hu2LDZwLvF5nHq01-0',
};

export const LongOptionsExample: TExample = {
    render(props) {
        return (
            <ContextMenu
                items={[
                    {
                        key: 'a',
                        icon: '🧑‍🌾',
                        label: 'Aaron Barkley',
                        description: 'Aaron Barkley is a diligent and hardworking farmer who is dedicated to his land.',
                    },
                    {
                        key: 'b',
                        icon: '👷',
                        label: 'Maximus "Max" Steel',
                        description:
                            'Maximus "Max" Steel is a seasoned construction worker who takes pride in building structures that stand the test of time.',
                    },
                    {
                        key: 'c',
                        icon: '👩‍⚕️',
                        label: 'Dr. Elizabeth "Liz" Sterling',
                        description: 'Dr. Elizabeth "Liz" Sterling is a compassionate and wise medical professional.',
                    },
                ]}
                onChoose={props.onChoose}
                onOpen={props.onOpen}
                onClose={props.onClose}
            >
                <button children="click me" />
            </ContextMenu>
        );
    },
    handlers: {
        onChoose: 'console',
        onOpen: 'console',
        onClose: 'console',
    },
    figma: 'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=0-1&mode=design&t=Hu2LDZwLvF5nHq01-0',
};

export const NestedExample: TExample = {
    render(props) {
        return (
            <ContextMenu
                items={[
                    {
                        key: 'a',
                        label: 'Option A',
                        options: [
                            { key: 'a1', label: 'Option A1' },
                            { key: 'a2', label: 'Option A2' },
                        ],
                    },
                    { key: 'b', label: 'Option B' },
                    {
                        key: 'c',
                        label: 'Option C',
                        options: [
                            { key: 'c1', label: 'Option C1' },
                            { key: 'c2', label: 'Option C2' },
                        ],
                    },
                ]}
                onChoose={props.onChoose}
                onOpen={props.onOpen}
                onClose={props.onClose}
            >
                <button children="click me" />
            </ContextMenu>
        );
    },
    handlers: {
        onChoose: 'console',
        onOpen: 'console',
        onClose: 'console',
    },
    figma: 'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System----2.0?type=design&node-id=21-1284&mode=design&t=gOMvZdG38ySvHFPd-0',
};

export const SelectedExample: TExample = {
    render(props) {
        return (
            <ContextMenu
                items={[
                    { key: 'a', label: 'Option A' },
                    { key: 'b', label: 'Option B' },
                    { key: 'c', label: 'Option C' },
                ]}
                onChoose={props.onChoose}
                onOpen={props.onOpen}
                onClose={props.onClose}
                defaultSelected={[{ key: 'b', label: 'Option B' }]}
            >
                <button children="click me" />
            </ContextMenu>
        );
    },
    handlers: {
        onChoose: 'console',
        onOpen: 'console',
        onClose: 'console',
    },
    figma: 'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=0-1&mode=design&t=Hu2LDZwLvF5nHq01-0',
};

export function TooltipExample() {
    return (
        <ContextMenu items={[{ key: 'like', label: 'Liked it?' }]} tooltip="👋🏼 Hello there!">
            <button children="w/ tooltip" />
        </ContextMenu>
    );
}

export const MultiSelectExample: TExample = {
    render(props) {
        const items: TContextMenuItem[] = [
            { key: 'open', label: 'Open' },
            { key: 'closed', label: 'Closed' },
            { key: 'snoozed', label: 'Snoozed' },
        ];
        return (
            <ContextMenu
                {...(props.async
                    ? { search: () => new Promise<TContextMenuItem[]>((r) => setTimeout(r, 1e3, items)) }
                    : { items })}
                defaultSelected={props.preselected ? [items[1], items[2]] : undefined}
                onSelect={props.onSelect}
            >
                <button children="Select status" />
            </ContextMenu>
        );
    },
    controls: {
        preselected: 'boolean',
        async: 'boolean',
    },
    handlers: {
        onSelect: 'console',
    },
    figma: 'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=0-1&mode=design&t=Hu2LDZwLvF5nHq01-0',
};

export const SearchExample: TExample = {
    render(props) {
        const items = [
            {
                key: 'red',
                label: 'Red',
                icon: <i style={{ background: 'red', width: '1em', height: '1em', display: 'inline-block' }} />,
            },
            {
                key: 'blue',
                label: 'Blue',
                icon: <i style={{ background: 'blue', width: '1em', height: '1em', display: 'inline-block' }} />,
                hotkey: hotkey('b', 'hidden'),
            },
            {
                key: 'green',
                label: 'Green',
                icon: <i style={{ background: 'green', width: '1em', height: '1em', display: 'inline-block' }} />,
            },
            {
                key: 'yellow',
                label: 'Yellow',
                icon: <i style={{ background: 'yellow', width: '1em', height: '1em', display: 'inline-block' }} />,
            },
            {
                key: 'orange',
                label: 'Orange',
                icon: <i style={{ background: 'orange', width: '1em', height: '1em', display: 'inline-block' }} />,
            },
            {
                key: 'purple',
                label: 'Purple',
                icon: <i style={{ background: 'purple', width: '1em', height: '1em', display: 'inline-block' }} />,
            },
            {
                key: 'pink',
                label: 'Pink',
                icon: <i style={{ background: 'pink', width: '1em', height: '1em', display: 'inline-block' }} />,
            },
            {
                key: 'brown',
                label: 'Brown',
                icon: <i style={{ background: 'brown', width: '1em', height: '1em', display: 'inline-block' }} />,
            },
            {
                key: 'black',
                label: 'Black',
                icon: <i style={{ background: 'black', width: '1em', height: '1em', display: 'inline-block' }} />,
            },
            {
                key: 'white',
                label: 'White',
                icon: <i style={{ background: 'white', width: '1em', height: '1em', display: 'inline-block' }} />,
            },
            {
                key: 'gray',
                label: 'Gray',
                icon: <i style={{ background: 'gray', width: '1em', height: '1em', display: 'inline-block' }} />,
            },
            {
                key: 'turquoise',
                label: 'Turquoise',
                icon: <i style={{ background: 'turquoise', width: '1em', height: '1em', display: 'inline-block' }} />,
            },
            {
                key: 'indigo',
                label: 'Indigo',
                icon: <i style={{ background: 'indigo', width: '1em', height: '1em', display: 'inline-block' }} />,
            },
            {
                key: 'cyan',
                label: 'Cyan',
                icon: <i style={{ background: 'cyan', width: '1em', height: '1em', display: 'inline-block' }} />,
            },
            {
                key: 'magenta',
                label: 'Magenta',
                icon: <i style={{ background: 'magenta', width: '1em', height: '1em', display: 'inline-block' }} />,
            },
            {
                key: 'maroon',
                label: 'Maroon',
                icon: <i style={{ background: 'maroon', width: '1em', height: '1em', display: 'inline-block' }} />,
            },
            {
                key: 'navy',
                label: 'Navy',
                icon: <i style={{ background: 'navy', width: '1em', height: '1em', display: 'inline-block' }} />,
            },
            {
                key: 'olive',
                label: 'Olive',
                icon: <i style={{ background: 'olive', width: '1em', height: '1em', display: 'inline-block' }} />,
            },
            {
                key: 'silver',
                label: 'Silver',
                icon: <i style={{ background: 'silver', width: '1em', height: '1em', display: 'inline-block' }} />,
            },
            {
                key: 'gold',
                label: 'Gold',
                icon: <i style={{ background: 'gold', width: '1em', height: '1em', display: 'inline-block' }} />,
            },
        ];
        return (
            <>
                <ContextMenu items={items} search onChoose={props.onChoose}>
                    <button children="w/ search" />
                </ContextMenu>

                <ContextMenu
                    items={items}
                    search
                    onChoose={props.onChoose}
                    searchSuffix={<HotkeyLabel hotkey={hotkey('ctrl+shift+z', 'hidden')} />}
                    defaultSelected={[items[1]]}
                >
                    <button children="search w/ hotkey" />
                </ContextMenu>

                <ContextMenu items={items} search onChoose={props.onChoose} searchSuffix={null}>
                    <button children="search w/o input icon" />
                </ContextMenu>
            </>
        );
    },
    handlers: {
        onChoose: 'console',
    },
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('ContextMenu', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
